import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Flex, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ChecklistItem } from 'src/analytics'
import {
  organizationsOrganizationsDestroyMutation,
  organizationsOrganizationsPartialUpdateMutation,
  organizationsOrganizationsRetrieveOptions,
  organizationsOrganizationsRetrieveQueryKey
} from 'src/api/main'
import { Loading, QueryErrorAlert } from 'src/components'
import { useUpdateUserContext } from 'src/features/organizations/hooks'
import { useReportJourneyStep } from 'src/features/workspaces/onboarding'
import { usePresignedUpload } from 'src/hooks/usePresignedUpload'
import { paths } from 'src/router/paths'
import { formatDate, useShowApiErrors } from 'src/utils'
import { uploadFiles } from 'src/utils/uploadFiles'

import { WorkspacePageShell, useWorkspaceRoute } from '../WorkspacePageShell'

import { BrandingSection } from './BrandingSection'
import { DangerZoneSection } from './DangerZoneSection'
import { DemoDataSection } from './DemoDataSection'
import {
  GeneralSettingsSection,
  type GeneralSettingsFormValues
} from './GeneralSettingsSection'

export const WorkspaceSettingsPage = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const { workspaceId, idValid } = useWorkspaceRoute()
  const { submitWebhook } = usePresignedUpload()
  const reportStep = useReportJourneyStep(workspaceId)
  const { updateUser } = useUpdateUserContext()

  const {
    data: organization,
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...organizationsOrganizationsRetrieveOptions({
      path: { id: workspaceId }
    }),
    enabled: idValid
  })

  const { mutate: updateWorkspace, isPending: isSaving } = useMutation({
    ...organizationsOrganizationsPartialUpdateMutation(),
    onSuccess: async () => {
      reportStep(ChecklistItem.SETTINGS)
      message.success(t('workspace.settings.saved'))
      void queryClient.invalidateQueries({
        queryKey: organizationsOrganizationsRetrieveQueryKey({
          path: { id: workspaceId }
        })
      })
      // The workspace name shown in the sidebar / switcher reads from
      // `useSessionContext().user.organizations`, not from a tanstack query —
      // re-fetch the session user so the new name propagates immediately.
      await updateUser({})
    },
    onError: (error) => showApiErrors(error)
  })

  const { mutate: deleteWorkspace, isPending: isDeleting } = useMutation({
    ...organizationsOrganizationsDestroyMutation(),
    onSuccess: async () => {
      message.success(t('workspace.settings.deleteSuccess'))
      // Same reasoning as above — refresh the session user so the deleted
      // workspace is removed from the sidebar list and the counter is
      // accurate before we navigate away.
      await updateUser({})
      void navigate(paths.workspaces.root)
    },
    onError: (error) => showApiErrors(error)
  })

  const handleSave = async (values: GeneralSettingsFormValues) => {
    if (!idValid) {
      return
    }

    const photoField = values.photo
    let photoId: number | null = null
    if (photoField && photoField.length > 0) {
      try {
        await uploadFiles(photoField, {
          submitWebhook,
          onError: (error) => showApiErrors(error)
        })
      } catch (error) {
        showApiErrors(error)
        return
      }
      const uploadResponse = photoField[0]?.response as
        | { id?: number }
        | undefined
      photoId = uploadResponse?.id ?? null
    }

    updateWorkspace({
      path: { id: workspaceId },
      body: {
        name: values.name,
        domain_name: values.domain_name,
        photo: photoId,
        default_language: values.default_language,
        enabled_languages: values.enabled_languages
      }
    })
  }

  const handleDelete = () => {
    if (!idValid) {
      return
    }
    deleteWorkspace({ path: { id: workspaceId } })
  }

  const renderContent = () => {
    if (isLoading) {
      return <Loading />
    }

    if (isError) {
      return <QueryErrorAlert onRetry={() => void refetch()} />
    }

    if (!organization) {
      return null
    }

    return (
      <Flex vertical gap={16}>
        <GeneralSettingsSection
          key={organization.id}
          organization={organization}
          onSave={(values) => void handleSave(values)}
          loading={isSaving}
        />
        <BrandingSection organization={organization} />
        <DemoDataSection organization={organization} />
        <DangerZoneSection
          workspaceId={workspaceId}
          workspaceName={organization.name}
          onDelete={handleDelete}
          deleteLoading={isDeleting}
        />
        <Flex justify="center" style={{ padding: '8px 0 16px' }}>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {t('workspace.settings.footer.created', {
              date: formatDate(organization.date_created)
            })}
          </Typography.Text>
        </Flex>
      </Flex>
    )
  }

  return (
    <WorkspacePageShell
      sectionTitle={t('workspace.settings.sectionTitle')}
      sectionDescription={t('workspace.settings.sectionDescription')}
    >
      {renderContent()}
    </WorkspacePageShell>
  )
}
